import { LitElement, css, html } from 'lit';
import { NumberedIconShape, SettingsChangeEvent } from '../../../types';
import { customElement, property, query } from 'lit/decorators';

import { DEFAULT_ICON_SIZE } from '../../../helpers/constants';
import { RangeSliderComponent } from '../../range-slider-component';
import { StateController } from '../../../helpers/state/state-management/state-controller';
import { isDefined } from '../../../helpers/utils';
import pointCache from '../../../helpers/state/point-cache';
import topicData from '../../../helpers/state/topic-data';

@customElement('icon-settings')
export class IconSettings extends LitElement {
    amplitude_callsite: string = 'Icon Settings';

    @property({ type: Boolean })
    open: boolean = false;

    @query('#iconSizeRangeSlider')
    iconSizeRangeSlider!: RangeSliderComponent;


	private readonly bindTopicState = new StateController(this, topicData);

    async onChange(e: CustomEvent<SettingsChangeEvent>) {
        const updatedValue = e.detail.updatedValue;
        const fieldToUpdate = e.detail.field;

        if (isDefined(e.detail.callback)) {
            await e.detail.callback();
        }

        await topicData.fetchAndUpdateTopicData({
            [fieldToUpdate]: updatedValue
        }, 'icon size');

        if (topicData.mapComponent) {
            await topicData.mapComponent.refreshMap();
            if (pointCache.allPoints.length > 0 && topicData.topic) {
                await pointCache.instantiatePointCache(topicData.topic, topicData.topicKey, true);
            }
        }
    }

    private async onChangeDefaultIconSize(e: Event) {
        const iconSize = (e.target as RangeSliderComponent).value;
        await topicData.fetchAndUpdateTopicData({
            icon_size: iconSize
        }, 'icon size');

        if (topicData.mapComponent) {
            await topicData.mapComponent.refreshMap();
            if (pointCache.allPoints.length > 0 && topicData.topic) {
                await pointCache.instantiatePointCache(topicData.topic, topicData.topicKey, true);
            }
        }
    };

    private async onChangeDefaultMarkerShape(e: CustomEvent) {
        const markerShape = (e.target as HTMLInputElement).value as NumberedIconShape;
        await topicData.fetchAndUpdateTopicData({
            marker_shape: markerShape
        }, 'marker shape');

        if (topicData.mapComponent) {
            await topicData.mapComponent.refreshMap();
            if (pointCache.allPoints.length > 0 && topicData.topic) {
                await pointCache.instantiatePointCache(topicData.topic, topicData.topicKey, true);
            }
        }
    }

    private async resetIconSizes() {
        await topicData.fetchAndUpdateTopicData({
            categories: topicData.topic?.categories?.map(category => {
                if (category.icon_size !== undefined) {
                    delete category.icon_size;
                }
                return category;
            })
        }, 'icon size');

        if (topicData.mapComponent) {
            await topicData.mapComponent.refreshMap();
            if (pointCache.allPoints.length > 0 && topicData.topic) {
                await pointCache.instantiatePointCache(topicData.topic, topicData.topicKey, true);
            }
        }
    }

    render() {
        const categoriesWithCustomSizes = topicData.topic?.categories?.filter(category => category.icon_size !== undefined);
        return html`
            <div class="style__wrapper">

                <div class="style__section">

                    <h4 class="sub-title"> Default Icon Size</h4>
                    ${(categoriesWithCustomSizes?.length ?? 0) > 0 ? html`
                        <div class="warning"> You have ${categoriesWithCustomSizes?.length ?? 0} ${(categoriesWithCustomSizes?.length ?? 0) > 1 ? 'categories' : 'category'} with custom icon sizes. Changing the default icon size will not affect these categories.
                            <span class="reset-button" @click="${this.resetIconSizes}">You can reset all category-specific icon sizes by clicking here.</span>
                        </div>
                    ` : ''}

                    <range-slider-component
                        id="iconSizeRangeSlider"
                        minString="Teeny Icons"
                        maxString="Mega Icons"
                        min=${10}
                        max=${100}
                        step=${1}
                        unit=${''}
                        .value=${topicData.topic?.icon_size ?? DEFAULT_ICON_SIZE}
                        @change=${this.onChangeDefaultIconSize}
                    ></range-slider-component>

                    <h4 class="sub-title">Default Marker Shape</h4>
                    <span class="info-paragraph">
                        Choose the default shape used for color-based category markers and numbered markers.
                    </span>
                    <proxi-selectfield
                        outlined
                        value="${topicData.topic?.marker_shape ?? NumberedIconShape.Circle}"
                        @selected=${this.onChangeDefaultMarkerShape}
                    >
                        <mwc-list-item value="${NumberedIconShape.Circle}">Circle</mwc-list-item>
                        <mwc-list-item value="${NumberedIconShape.Square}">Square</mwc-list-item>
                        <mwc-list-item value="${NumberedIconShape.Star}">Star</mwc-list-item>
                        <mwc-list-item value="${NumberedIconShape.Diamond}">Diamond</mwc-list-item>
                        <mwc-list-item value="${NumberedIconShape.Hexagon}">Hexagon</mwc-list-item>
                    </proxi-selectfield>

                    <marker-cluster-settings
                    ></marker-cluster-settings>

                    <featured-point-color @on-change-value=${this.onChange}></featured-point-color>

                </div>

            </div>
            </div>

        `;
    }

    static get styles() {
        return css`
            :host {
                flex: 1;
            }

            h3 {
                text-align: center;
                margin: .5rem;
            }

            .title {
                color: var(--main-primary-color);
                text-align: center;
                font-size: 1rem;
                font-weight: 700;
                line-height: 130%;
                margin: 5px 0;
                text-align: left;
            }

            .warning {
                border: 1px solid var(--main-invalid-color);
                margin-top: 0.5rem;
                margin-bottom: 0.5rem;
                padding: 0.5rem;
                background-color: var(--main-warning-color);
                font-size: 0.8rem;
            }

            .reset-button {
                cursor: pointer;
                text-decoration: underline;
            }

            .info-paragraph {
                font-size: 0.8rem;
                color: var(--theme-input-color);
                margin-top: 0;
                margin-bottom: 0;
            }

            .sub-title {
                margin-bottom: 5px;
                margin-top: 5px;
                color: var(--theme-input-color);
            }

            .style__wrapper {
                height:100%;
                display: flex;
                flex-direction: column;
                justify-content: center;
            }

            .mapStyleCollection {
                display: grid;
                grid-template-columns: 1fr 1fr 1fr 1fr;
                grid-gap: 0.5rem;

            }

            .style__section {
                padding-right: 20px;
                flex: 1;
                overflow-y: auto;
                overflow-x: hidden;
            }

            .map-style-component__tabs {
                display: flex;
                justify-content: flex-start;
            }

            .map-style-component__tab {
                padding-bottom: 0.25rem;
                line-height: 1.5;
                color: var(--theme-lighter-text-color);
                cursor: pointer;
                margin-right: 8px;
                flex: 1;
                text-align: center;
            }

            .map-style-component__tab.active {
                font-weight: 700;
                border-bottom: 2px solid var(--main-secondary-color);
                color: var(--main-secondary-color);
            }

            .mapStyleItem {
                text-align: center;
            }

            .mapStyleItem.active {
                font-weight: 700;
            }

            .mapStyleItem img {
                width: 100%;
                height: 3.5rem;
                border-radius: 0.75rem;
                border: 2px solid transparent;
                box-shadow: 0px 2px 8px -4px rgba(31, 27, 45, 0.12);
            }

            .header-with-info {
                display: flex;
                flex-direction: row;
                align-items: center;
                margin-top: 5px;
                margin-bottom: 5px;
            }

            .mapStyleItem.active img {
                border: 2px solid var(--main-primary-color);
            }

            .icon__actions {
                padding-top: 18px;
                display: flex;
                justify-content: space-evenly;
            }
		    .input {
                width: 100%;
                padding: auto 5px;
            }

            .style__actions {
                display: flex;
                flex-direction: row;
                justify-content: flex-end;
                padding: 1rem 0.5rem;
                gap: 0.5rem;
            }
        `;
    }
}
